import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Heart, MessageCircle } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import CommentThread from "./CommentThread";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArticleEngagement = ({ slug }) => {
  const [reactions, setReactions] = useState(0);
  const [comments, setComments] = useState([]);
  const [reacted, setReacted] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(`article_react_${slug}`) === "1"
  );
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/articles/${slug}/meta`);
      setReactions(res.data.reactions || 0);
      setComments(res.data.comments || []);
    } catch (err) {
      console.error("Failed to load article engagement", err);
    }
  }, [slug]);

  useEffect(() => {
    setReacted(localStorage.getItem(`article_react_${slug}`) === "1");
    load();
  }, [slug, load]);

  const react = async () => {
    const delta = reacted ? -1 : 1;
    const next = !reacted;
    setReacted(next);
    setReactions((r) => Math.max(0, r + delta));
    try {
      const res = await axios.post(`${API}/articles/${slug}/react`, { delta });
      setReactions(res.data.reactions);
      localStorage.setItem(`article_react_${slug}`, next ? "1" : "0");
    } catch (err) {
      console.error("Failed to save reaction", err);
      setReacted(!next);
      setReactions((r) => Math.max(0, r - delta));
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email to comment.");
      return;
    }
    setPosting(true);
    try {
      const res = await axios.post(`${API}/articles/${slug}/comment`, { name: name.trim(), email: email.trim(), text: text.trim() });
      setComments((c) => [...c, res.data]);
      setText("");
      setName("");
      setEmail("");
    } catch (err) {
      toast.error("Couldn't post your comment. Try again.");
    } finally {
      setPosting(false);
    }
  };

  const scrollToComments = () => {
    const el = document.getElementById("article-comments");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="max-w-2xl mx-auto px-6">
      {/* Reaction / comment bar */}
      <div className="flex items-center gap-3 py-6 border-y border-neutral-300 mt-4">
        <button
          onClick={react}
          data-testid="article-react-button"
          aria-label="React with a heart"
          className={`inline-flex items-center gap-2 font-sans-ui text-sm rounded-full px-5 py-2.5 border transition-all duration-200 hover:scale-[1.03] ${
            reacted
              ? "bg-rose-100 border-rose-200 text-rose-600"
              : "border-neutral-300 text-neutral-700 hover:border-neutral-500"
          }`}
        >
          <Heart size={16} fill={reacted ? "currentColor" : "none"} />
          {reactions > 0 && <span>{reactions}</span>}
        </button>
        <button
          onClick={scrollToComments}
          data-testid="article-comment-button"
          className="inline-flex items-center gap-2 font-sans-ui text-sm rounded-full px-5 py-2.5 border border-neutral-300 text-neutral-700 hover:border-neutral-500 transition-all duration-200 hover:scale-[1.03]"
        >
          <MessageCircle size={16} />
          {comments.length > 0 ? comments.length : "Comment"}
        </button>
      </div>

      {/* Comments */}
      <div id="article-comments" className="pt-10 pb-4 scroll-mt-28">
        <h3 className="font-display text-2xl text-neutral-900" style={{ fontWeight: 700 }}>
          {comments.length} {comments.length === 1 ? "comment" : "comments"}
        </h3>

        <div className="mt-6">
          <CommentThread
            comments={comments}
            setComments={setComments}
            apiBase={`${API}/articles/${slug}`}
          />
        </div>

        <form onSubmit={addComment} className="mt-8 flex flex-col gap-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={40}
              placeholder="Your name or initials (optional)"
              data-testid="article-comment-name"
              className="h-11 bg-white/70 border-neutral-300 font-sans-ui focus-visible:ring-neutral-900"
            />
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email (required)"
              required
              data-testid="article-comment-email"
              className="h-11 bg-white/70 border-neutral-300 font-sans-ui focus-visible:ring-neutral-900"
            />
          </div>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Share a thought..."
            data-testid="article-comment-text"
            className="bg-white/70 border-neutral-300 font-display resize-none focus-visible:ring-neutral-900"
          />
          <button
            type="submit"
            disabled={posting}
            data-testid="article-comment-submit"
            className="self-start bg-neutral-900 hover:bg-neutral-700 disabled:opacity-60 text-white font-sans-ui text-sm px-7 py-3 rounded-full transition-colors"
          >
            {posting ? "Posting\u2026" : "Post comment"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ArticleEngagement;
